/**
 * Axios Configuration with Interceptors
 * Sets up global request/response interceptors for error handling
 */
import axios from 'axios';
import { BASE_URL } from '../url/baseurl';
import { handleApiError, ErrorTypes } from './errorHandler';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    // Add token to headers if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
        data: config.data,
        params: config.params
      });
    }

    return config;
  },
  (error) => {
    // Handle request error
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle global response errors
axiosInstance.interceptors.response.use(
  (response) => {
    // Log successful response in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API Response] ${response.config.url}`, response.data);
    }

    // Check if response indicates an error even with 200 status
    if (response.data && response.data.code && response.data.code !== 200) {
      const error = new Error(response.data.message || 'API returned an error');
      error.response = response;
      return Promise.reject(error);
    }

    return response;
  },
  (error) => {
    // Handle response errors globally
    const formattedError = handleApiError(error, error.config?.url || 'API', {
      logError: true
    });

    // Handle specific error types
    if (formattedError.errorType === ErrorTypes.AUTH_ERROR) {
      // Token expired or invalid - clear storage and redirect
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        localStorage.clear();
        window.location.href = '/login';
      }
    }

    return Promise.reject(formattedError);
  }
);

export default axiosInstance;



